import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  CircularProgress,
  Grid,
  Icon,
  Icons,
  SimpleDialog,
  Typography,
  styled,
} from '@nextiva/next-ui-components';
import { useFeature } from '@nextiva/feature-toggle';
import {
  useSnackbar,
  useTeamEnableSMS,
  useTeamDisableSMS,
  useTeamSMSLicense,
} from 'hooks';
import { SwitchWrapper } from 'components/Switch';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { pxToRem } from 'utils/pxToRemConverter';
import { formatToPhoneNumber } from 'utils/phone-number';
import Drawer from 'containers/Drawer';
import CreateTeam from 'containers/pages/Teams/CreateTeam';
import { actionTypes } from 'containers/pages/Teams/redux';
import { EDIT_TEAM } from 'constants/drawerTitles';
import { getLicensesByUsers } from 'api/licenseService';
import { TEAM_SMS } from 'constants/features';
import { getSMSCampaignStatus } from 'services';
import RegistrationModal from 'components/WizardModals/RegistrationModal';
import PendingDialog from 'components/WizardModals/PendingDialog';
import { pendingStatuses, submittedStatuses } from 'constants/smsWizard';

const ModalMessage = styled(Typography)`
  margin: 24px 0px;
`;
const ModalContentMessage = styled(Typography)`
  margin-bottom: 24px;
`;

const SubText = styled(Typography)`
  font-weight: 400;
  font-size: ${pxToRem('14px')};
  line-height: 24px;
  letter-spacing: 0.21px;
  color: ${({ theme }) => theme.palette.blueGrey.A400};
`;

const StyledIcon = styled(Icon)`
  margin-right: 10px;
`;

const StyledProgress = styled(CircularProgress)`
  position: absolute;
  top: 78%;
`;

const StyledMessageProLink = styled('a')`
  text-decoration: underline;
`;
const StyledMessageProTypography = styled(Typography)`
  line-height: 1.875rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #333333;
`;

const TeamSMSToggle = ({
  phoneNumber,
  locationId,
  setSMSError,
  smsEnabled,
  setSmsEnabled,
  proceedToSave,
  isLoading,
  confirmSMS,
  deletePrimary,
  cancelChange,
  cancelDelete,
  huntGroupId,
}) => {
  const setSnackbar = useSnackbar();
  const formattedNum = formatToPhoneNumber(phoneNumber);
  const [showDrawer, setShowDrawer] = useState(false);
  const [declinedState, setDeclinedState] = useState(false);
  const [declinedModal, setDeclinedModal] = useState(false);
  const [allUserHaveTeamSMS, setAllUserHaveTeamSMS] = useState(false);
  const [userLicensesModal, setUserLicensesModal] = useState(false);
  const [openRegistrationModal, setOpenRegistrationModal] = useState(false);
  const [openPendingDialog, setOpenPendingDialog] = useState(false);
  const history = useHistory();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const baseUserList = queryClient.getQueryData('getUsersBasicInfo');
  const { editTeamInfo: teamInfo, viewList } = useSelector(
    (state) => state.teamsReduxReducer,
  );

  let editTeamInfo = teamInfo;
  if (viewList) {
    editTeamInfo = viewList.find(
      (team) => team.durableCallGroupId === huntGroupId,
    );
    dispatch({
      type: actionTypes.SET_EDIT_TEAM_INFO,
      editTeamInfo,
    });
  }

  const {
    corpAcctNbr,
    featureState: { teamsSMSLicensing },
  } = useContext(FeatureContext);
  const { isEnabled: unblockSmsEnablement } = useFeature({
    name: 'PlatformAdmin.SMSCampaignRegistry.unblockSMSEnablement',
  });

  const { data: smsCampaignStatus } = useQuery(
    ['smsCampaignStatus'],
    () => getSMSCampaignStatus(corpAcctNbr),
    {
      onSuccess: (smsCampaignStatusResponse) => {
        if (
          smsCampaignStatusResponse === 'DECLINED' ||
          smsCampaignStatusResponse === 'PENDING' ||
          smsCampaignStatusResponse === 'NOT_SUBMITTED'
        ) {
          setDeclinedState(true);
        }
      },
    },
  );

  useEffect(() => {
    if (editTeamInfo?.assignedProfileIds && teamsSMSLicensing && baseUserList) {
      const selectedUserUUIDs = baseUserList
        .filter((baseUser) =>
          editTeamInfo.assignedProfileIds.includes(baseUser.profileId),
        )
        .map((baseUser) => baseUser.nextivaUUID);
      getLicensesByUsers(corpAcctNbr, selectedUserUUIDs.join(',')).then(
        ({ userProductFeaturesMap }) => {
          setAllUserHaveTeamSMS(
            selectedUserUUIDs.every(
              (uuid) =>
                !!userProductFeaturesMap[uuid].products
                  ?.find((product) => product.product === 'SMS')
                  ?.features.find((feature) => feature.name === TEAM_SMS),
            ),
          );
        },
      );
    }
  }, []);

  const { mutateAsync: enableSMSAsync } = useTeamEnableSMS(
    setSnackbar,
    formattedNum,
    corpAcctNbr,
  );
  const { mutateAsync: disableSMSAsync } = useTeamDisableSMS(
    setSnackbar,
    formattedNum,
    corpAcctNbr,
  );
  const { noTeamSMSLicenses } = useTeamSMSLicense();

  const toggleTeamDrawer = useCallback(() => {
    setShowDrawer((e) => !e);
  }, []);

  const disableSMSComplete = async () => {
    await disableSMSAsync({
      phoneNumber,
    });
    setSmsEnabled(false);
  };

  const enableSMSComplete = async () => {
    await enableSMSAsync({
      phoneNumber,
    });
    if (!allUserHaveTeamSMS) {
      setUserLicensesModal(true);
    }
    setSmsEnabled(true);
  };

  const handleSMSChange = async (checked) => {
    const enableSms =
      smsCampaignStatus &&
      (unblockSmsEnablement ||
        (smsCampaignStatus === 'ACCEPTED' && !unblockSmsEnablement));
    if (checked && enableSms) {
      await enableSMSComplete();
      if (unblockSmsEnablement) {
        // eslint-disable-next-line no-unused-expressions
        openRegistrationModal && setOpenRegistrationModal(false);
        // eslint-disable-next-line no-unused-expressions
        openPendingDialog && setOpenPendingDialog(false);
      }
    } else if (phoneNumber) {
      await disableSMSComplete();
    }
  };

  const handleUnblockSmsChange = async (checked) => {
    if (submittedStatuses.includes(smsCampaignStatus) && !smsEnabled) {
      setOpenRegistrationModal(true);
    } else if (pendingStatuses.includes(smsCampaignStatus) && !smsEnabled) {
      setOpenPendingDialog(true);
    } else {
      await handleSMSChange(checked);
    }
  };

  const handleSmsEnablement = async (checked) => {
    if (declinedState && !smsEnabled) {
      setDeclinedModal(true);
    } else {
      await handleSMSChange(checked);
    }
  };
  const changeSMS = async (event) => {
    const { checked } = event.target;
    if (checked && !phoneNumber) {
      setSMSError('A phone number is required');
      return;
    }
    if (unblockSmsEnablement) {
      handleUnblockSmsChange(checked);
    } else {
      handleSmsEnablement(checked);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Grid container direction="row" xs={12} justifyContent="space-between">
          <StyledMessageProTypography variant="body1">
            Message Pro
          </StyledMessageProTypography>
          <span>
            {noTeamSMSLicenses && teamsSMSLicensing && (
              <StyledIcon
                data-testid="PlatformAdminUI-TeamsMessaging-Toggle-KeyIcon"
                icon={Icons.keySolid}
                size={14}
              />
            )}
            <SwitchWrapper
              checked={smsEnabled}
              onChange={changeSMS}
              disabled={noTeamSMSLicenses && teamsSMSLicensing}
              data-testid="PlatformAdminUI-Messaging-PhoneSMS-ToggleSwitcher"
            />
          </span>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SubText variant="p">
          Enable or disable Message Pro services for this primary number
        </SubText>
      </Grid>
      {!phoneNumber && !!locationId && (
        <Grid item xs={12} style={{ marginTop: '24px' }}>
          <Alert severity="info">
            To enable Message Pro (SMS), you must assign a primary phone number.
          </Alert>
        </Grid>
      )}
      {noTeamSMSLicenses && (
        <Grid item xs={12} style={{ marginTop: '24px' }}>
          <Alert severity="info">
            <span>
              Want to route incoming texts to a team? Contact us at
              1-800-799-0600 to get started. Learn more about{' '}
              <StyledMessageProLink
                href="https://www.nextiva.com/products/message-pro"
                target="_blank"
                rel="noreferrer"
                data-testid="PlatformAdminUI-TeamSMSToggle-MessagePro-LearnMore"
              >
                Message Pro.
              </StyledMessageProLink>
            </span>
          </Alert>
        </Grid>
      )}
      <SimpleDialog
        cancelButtonProps={{
          onClick: cancelChange,
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-CancelButton',
        }}
        cancelText="Cancel"
        confirmButtonProps={{
          color: 'primary',
          onClick: proceedToSave,
          disabled: isLoading,
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-ConfirmButton',
        }}
        confirmText="Yes, change"
        title="Change team phone number"
        open={confirmSMS}
      >
        <ModalMessage variant="body2">
          By changing this number, you will need to re-enable messaging for this
          team.
        </ModalMessage>
        <ModalMessage variant="body2">
          Are you sure you want to change this number?
        </ModalMessage>
        {isLoading && <StyledProgress size={20} />}
      </SimpleDialog>
      <SimpleDialog
        cancelButtonProps={{
          onClick: cancelDelete,
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-RemoveCancelButton',
        }}
        cancelText="Cancel"
        confirmButtonProps={{
          color: 'danger',
          onClick: proceedToSave,
          disabled: isLoading,
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-RemoveCancelButton',
        }}
        confirmText="Yes, remove"
        title="Remove team phone number"
        open={deletePrimary}
      >
        <ModalMessage variant="body2">
          By removing this number, messaging will also be disabled for this
          team.
        </ModalMessage>
        <ModalMessage variant="body2">
          Are you sure you want to remove this number?
        </ModalMessage>
        {isLoading && <StyledProgress size={20} />}
      </SimpleDialog>
      <SimpleDialog
        cancelButtonProps={{
          onClick: () => setUserLicensesModal(false),
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-EditCancelButton',
        }}
        cancelText="No, thanks"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => {
            setUserLicensesModal(false);
            toggleTeamDrawer();
          },
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-EditConfirmButton',
        }}
        confirmText="Yes, view users"
        title="Missing user licenses"
        maxWidth="xs"
        open={userLicensesModal}
      >
        <ModalContentMessage variant="body2">
          Message Pro has been successfully enabled for this team. However, some
          users on this team are not enabled for Message Pro.
        </ModalContentMessage>
        <ModalMessage variant="body2">
          Would you like to view which users on this team are enabled for
          Message Pro?
        </ModalMessage>
      </SimpleDialog>
      <SimpleDialog
        cancelButtonProps={{
          onClick: () => setDeclinedModal(false),
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-DeclinedCancelButton',
        }}
        confirmButtonProps={{
          color: 'primary',
          onClick: () => history.push('/messaging'),
          'data-testid': 'PlatformAdminUI-TeamSMSToggle-DeclineContinueButton',
        }}
        cancelText="Cancel"
        confirmText="Yes, continue"
        title="Enable text messaging"
        maxWidth="xs"
        open={declinedModal}
      >
        <ModalMessage variant="body2">
          Businesses are required to register their accounts with{' '}
          <b>The Campaign Registry</b> to enable text messaging. The process can
          last between 10-30 business days.{' '}
          <a
            href="https://www.nextiva.com/support/articles/10dlc-10-digit-long-code-faqs.html"
            target="_blank"
            rel="noreferrer"
            data-testid="PlatformAdminUI-TeamSMSToggle-LearnMore"
          >
            Learn more.
          </a>
        </ModalMessage>
        <ModalMessage variant="body2">
          Would you like to register now? If already registered, check your
          status.
        </ModalMessage>
      </SimpleDialog>
      <Drawer
        title={EDIT_TEAM}
        toggleDrawer={toggleTeamDrawer}
        open={showDrawer}
      >
        <CreateTeam toggleDrawer={toggleTeamDrawer} type={EDIT_TEAM} />
      </Drawer>
      <RegistrationModal
        open={openRegistrationModal}
        onClose={() => setOpenRegistrationModal(false)}
        handleSMSChange={handleSMSChange}
      />
      <PendingDialog
        open={openPendingDialog}
        onClose={() => setOpenPendingDialog(false)}
        handleSMSChange={handleSMSChange}
      />
    </>
  );
};

export default TeamSMSToggle;
